import type {
  ButtonHTMLAttributes,
  ComponentProps,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { useId } from 'react';
import { Link } from 'react-router';
import { ITEM_KIND_LABELS } from '../../../shared/constants.ts';
import type { ApiError } from '../api.ts';

const cx = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ');

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const variants: Record<Variant, string> = {
  primary: 'bg-night-800 text-white hover:bg-night-700 disabled:bg-night-800/50',
  secondary: 'bg-white text-night-800 ring-1 ring-night-800/20 hover:bg-night-100',
  ghost: 'text-night-800 hover:bg-night-100',
  danger: 'bg-red-700 text-white hover:bg-red-800',
};
const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed';

export function Button({
  variant = 'primary',
  className,
  pending,
  disabled,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; pending?: boolean }) {
  // Under ett pågående anrop är knappen alltid spärrad, även om disabled={false} skickas in.
  return (
    <button
      className={cx(buttonBase, variants[variant], className)}
      {...props}
      disabled={pending || disabled}
    >
      {pending && <Spinner small />}
      {children}
    </button>
  );
}

export function LinkButton({
  to,
  variant = 'primary',
  className,
  children,
}: {
  to: string;
  variant?: Variant;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={cx(buttonBase, variants[variant], className)}>
      {children}
    </Link>
  );
}

export function Spinner({ small }: { small?: boolean }) {
  return (
    <span
      role="status"
      aria-label="Laddar"
      className={cx(
        'inline-block animate-spin rounded-full border-2 border-current border-r-transparent',
        small ? 'h-4 w-4' : 'h-8 w-8 text-night-700',
      )}
    />
  );
}

export function Loading() {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
}

export function Alert({
  kind = 'error',
  children,
}: {
  kind?: 'error' | 'success' | 'info';
  children: ReactNode;
}) {
  const styles = {
    error: 'bg-red-50 text-red-900 ring-red-200',
    success: 'bg-aurora-100 text-aurora-600 ring-aurora-400/40',
    info: 'bg-night-100 text-night-900 ring-night-800/10',
  };
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={cx('rounded-lg px-4 py-3 text-sm ring-1', styles[kind])}
    >
      {children}
    </div>
  );
}

export function ErrorMessage({ error }: { error: ApiError | null }) {
  if (!error) return null;
  return <Alert>{error.message}</Alert>;
}

export function Card({ children, className, id }: { children: ReactNode; className?: string; id?: string }) {
  return (
    <div
      id={id}
      className={cx('rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:p-7', className)}
    >
      {children}
    </div>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
}

export function Field({ label, error, hint, ...props }: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={!!error}
        aria-describedby={error || hint ? `${id}-desc` : undefined}
        className={cx(
          'mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-base shadow-sm outline-none focus:ring-2 focus:ring-aurora-400',
          error ? 'border-red-500' : 'border-night-800/20',
        )}
        {...props}
      />
      {(error || hint) && (
        <p id={`${id}-desc`} className={cx('mt-1 text-sm', error ? 'text-red-700' : 'text-muted')}>
          {error || hint}
        </p>
      )}
    </div>
  );
}

export function SelectField({
  label,
  error,
  hint,
  options,
  ...props
}: FieldProps &
  SelectHTMLAttributes<HTMLSelectElement> & {
    options: readonly string[] | { value: string; label: string }[];
  }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={!!error}
        className={cx(
          'mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-base shadow-sm outline-none focus:ring-2 focus:ring-aurora-400',
          error ? 'border-red-500' : 'border-night-800/20',
        )}
        {...props}
      >
        {options.map((o) =>
          typeof o === 'string' ? (
            <option key={o} value={o}>
              {o}
            </option>
          ) : (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ),
        )}
      </select>
      {hint && !error && <p className="mt-1 text-sm text-muted">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-700">{error}</p>}
    </div>
  );
}

export function TextArea({
  label,
  error,
  hint,
  className,
  ...props
}: FieldProps & ComponentProps<'textarea'>) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      <textarea
        id={id}
        aria-invalid={!!error}
        className={cx(
          'mt-1 block w-full rounded-lg border bg-white px-3 py-2.5 text-base shadow-sm outline-none focus:ring-2 focus:ring-aurora-400',
          error ? 'border-red-500' : 'border-night-800/20',
          className,
        )}
        {...props}
      />
      {(error || hint) && (
        <p className={cx('mt-1 text-sm', error ? 'text-red-700' : 'text-muted')}>{error || hint}</p>
      )}
    </div>
  );
}

export function ProgressBar({ percent, label }: { percent: number; label?: string }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Framsteg'}
      className="h-2.5 w-full overflow-hidden rounded-full bg-night-100"
    >
      <div
        className="h-full rounded-full bg-gradient-to-r from-aurora-500 via-aurora-400 to-violet-aurora transition-all"
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
  );
}

export function ProgressRing({ percent, size = 120 }: { percent: number; size?: number }) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${percent} procent klart`}
    >
      <defs>
        <linearGradient id="ring" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#2e8b6e" />
          <stop offset="0.6" stopColor="#3fb68b" />
          <stop offset="1" stopColor="#7a5cc7" />
        </linearGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="url(#ring)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - percent / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-white text-2xl font-bold"
      >
        {percent}%
      </text>
    </svg>
  );
}

const KIND_ICONS: Record<string, string> = {
  page: 'M6 4h9l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zm8 0v5h5M8 13h8M8 17h6',
  video: 'M4 6h12a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1zm13 4 4-2v8l-4-2',
  audio: 'M9 18V6l10-2v12M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm10-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  quiz: 'M9 9a3 3 0 1 1 4 2.8c-.6.3-1 .9-1 1.5V14m0 4h.01M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z',
  discussion: 'M4 5h16v10H8l-4 4V5zm4 4h8M8 12h5',
  assignment: 'M9 4h6l1 2h3v15H5V6h3l1-2zm-1 9 3 3 5-6',
  meeting: 'M7 3v3m10-3v3M4 8h16M5 5h14v15H5V5zm4 7h2v2H9z',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  deepdive: 'M4 19V5a1 1 0 0 1 1-1h6l2 2h6a1 1 0 0 1 1 1v12M4 19h16M9 12l2 2 4-4',
  tryout: 'M3 20h18M5 20V9l7-5 7 5v11M9 20v-6h6v6M9 10h.01M15 10h.01',
  reflection: 'M12 3a6 6 0 0 0-3.5 10.9V17h7v-3.1A6 6 0 0 0 12 3zM9.5 21h5',
};

export function KindIcon({ kind, className }: { kind: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cx('h-5 w-5 shrink-0', className)} aria-hidden="true">
      <path
        d={KIND_ICONS[kind] ?? KIND_ICONS.page}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const kindLabel = (kind: string) => ITEM_KIND_LABELS[kind] ?? kind;

export function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cx('h-5 w-5', className)} aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        d="m7.5 12.5 3 3 6-6.5"
        fill="none"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PageTitle({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-night-900 sm:text-3xl">{children}</h1>
      {sub && <p className="mt-1 text-muted">{sub}</p>}
    </div>
  );
}

export function Badge({
  children,
  tone = 'grey',
}: {
  children: ReactNode;
  tone?: 'grey' | 'green' | 'amber' | 'blue' | 'red';
}) {
  const tones = {
    grey: 'bg-night-100 text-night-900',
    green: 'bg-aurora-100 text-aurora-600',
    amber: 'bg-amber-100 text-amber-900',
    blue: 'bg-blue-100 text-blue-900',
    red: 'bg-red-100 text-red-900',
  };
  return (
    <span className={cx('inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold', tones[tone])}>
      {children}
    </span>
  );
}

export { cx };
